import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey, jadeGreen, lightBlue, volcano } from '../../design-system/theme';
import { InfographicViewer } from './InfographicViewer';
import {
  KEY_CONCEPTS_SYNTAX,
  TENANT_HIERARCHY_SYNTAX,
  EVIDENCE_TYPES_SYNTAX,
  USER_ROLES_SYNTAX,
  DOCUMENT_TYPES_SYNTAX,
} from './infographicData';

// ===== STYLED COMPONENTS =====

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ConceptCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid ${neutral[3]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const IconCircle = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;

const SectionTitle = styled.div`
  flex: 1;
  
  h2 {
    margin: 0 0 4px 0;
    font-size: 22px;
    font-weight: 700;
    color: ${theme.textPrimary};
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: ${theme.textSecondary};
    line-height: 1.5;
  }
`;

const ConceptGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const ConceptItem = styled.div<{ $color: string }>`
  background: ${neutral[1]};
  border-radius: 12px;
  padding: 24px;
  border-left: 4px solid ${props => props.$color};
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  h3 {
    margin: 0 0 8px 0;
    font-size: 17px;
    font-weight: 600;
    color: ${theme.textPrimary};
    display: flex;
    align-items: center;
    gap: 10px;
    
    .emoji {
      font-size: 22px;
    }
  }
  
  p {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: ${theme.textSecondary};
    line-height: 1.6;
  }
  
  .example {
    background: white;
    border-radius: 8px;
    padding: 12px;
    font-size: 13px;
    color: ${blueGrey[7]};
    
    strong {
      color: ${theme.textPrimary};
      font-weight: 600;
    }
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.$active ? `
    background: ${theme.primary};
    color: white;
    box-shadow: 0 4px 12px rgba(54, 58, 80, 0.3);
  ` : `
    background: ${neutral[2]};
    color: ${theme.textSecondary};
    
    &:hover {
      background: ${neutral[3]};
    }
  `}
`;

const DiagramSection = styled.div`
  margin-top: 24px;
  padding: 24px;
  background: ${neutral[1]};
  border-radius: 12px;
`;

const DiagramTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RelationshipDiagram = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  flex-wrap: wrap;
`;

const EntityBox = styled.div<{ $color: string }>`
  padding: 16px 24px;
  background: ${props => props.$color}15;
  border: 2px solid ${props => props.$color};
  border-radius: 12px;
  text-align: center;
  min-width: 140px;
  
  .emoji {
    font-size: 28px;
    margin-bottom: 8px;
  }
  
  .label {
    font-size: 14px;
    font-weight: 600;
    color: ${theme.textPrimary};
  }
  
  .sublabel {
    font-size: 12px;
    color: ${theme.textSecondary};
    margin-top: 4px;
  }
`;

const Arrow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: ${theme.textSecondary};
  
  .line {
    width: 40px;
    height: 2px;
    background: ${neutral[4]};
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      right: -2px;
      top: -4px;
      border: 5px solid transparent;
      border-left-color: ${neutral[4]};
    }
  }
  
  .label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const DefinitionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

const DefinitionItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  background: ${neutral[1]};
  border-radius: 12px;
  align-items: flex-start;
  
  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  
  .content {
    flex: 1;
    
    h4 {
      margin: 0 0 6px 0;
      font-size: 16px;
      font-weight: 600;
      color: ${theme.textPrimary};
    }
    
    p {
      margin: 0;
      font-size: 14px;
      color: ${theme.textSecondary};
      line-height: 1.6;
    }
    
    .tags {
      display: flex;
      gap: 8px;
      margin-top: 12px;
      flex-wrap: wrap;
    }
  }
`;

const Tag = styled.span<{ $color: string }>`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
`;

const InfoAlert = styled.div`
  padding: 16px 20px;
  background: ${lightBlue[1]};
  border-radius: 12px;
  border-left: 4px solid ${lightBlue[5]};
  margin-top: 24px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  
  .icon {
    font-size: 20px;
    flex-shrink: 0;
  }
  
  .content {
    font-size: 14px;
    line-height: 1.6;
    color: ${theme.textPrimary};
    
    strong {
      font-weight: 600;
    }
  }
`;

// ===== CONCEPT DATA =====

type ConceptTab = 'entities' | 'documents' | 'evidence' | 'roles';

const concepts = {
  entities: [
    {
      emoji: '🏢',
      title: 'Tenant',
      description: 'Your organization using the consent platform. Each tenant has completely isolated data, separate branding, and independent configuration.',
      example: 'Example: "HealthCare Plus Hospital" or "TechCorp Inc."',
      color: blueGrey[7],
    },
    {
      emoji: '🛡️',
      title: 'Data Fiduciary',
      description: 'An entity that determines the purpose and means of processing personal data. Under DPDP Act, they bear legal responsibility for data protection compliance.',
      example: 'Example: A hospital collecting patient data for treatment.',
      color: volcano[6],
    },
    {
      emoji: '👤',
      title: 'Data Owner (Principal)',
      description: 'The individual whose personal data is being collected. Also known as "Data Principal" under the DPDP Act. They have rights over their data.',
      example: 'Example: A patient giving consent for medical records.',
      color: jadeGreen[5],
    },
    {
      emoji: '📁',
      title: 'Project',
      description: 'A specific data collection initiative or program. Projects group related consent activities and define the purpose of data collection.',
      example: 'Example: "Clinical Trial Alpha" or "Customer Survey 2026".',
      color: lightBlue[5],
    },
    {
      emoji: '📝',
      title: 'Template',
      description: 'A reusable consent document configuration. Templates define what consent text, policies, and terms are shown to data owners.',
      example: 'Example: Privacy policy template, Terms of service template.',
      color: blueGrey[5],
    },
    {
      emoji: '🔗',
      title: 'Consent Form',
      description: 'A generated URL that combines one or more templates with evidence requirements. This is what you send to data owners to collect consent.',
      example: 'Example: A unique URL sent via email to a patient.',
      color: volcano[5],
    },
    {
      emoji: '✅',
      title: 'Consent Artifact',
      description: 'The permanent, immutable proof of consent as per Meity BRD. Includes full audit trail, evidence, timestamps, device information, and data erasure status.',
      example: 'Stored securely for 7+ years for compliance.',
      color: jadeGreen[6],
    },
    {
      emoji: '📊',
      title: 'Evidence',
      description: 'Proof that consent was given. Can include digital signatures, OTP verification, video recordings, or audio recordings.',
      example: 'Example: Signed consent + SMS OTP verification.',
      color: lightBlue[6],
    },
  ],
  documents: [
    { emoji: '📜', title: 'Privacy Policy', description: 'Comprehensive disclosure of data handling practices' },
    { emoji: '✍️', title: 'Consent Text', description: 'Specific consent language for data processing' },
    { emoji: '⚖️', title: 'Terms of Service', description: 'Legal agreement and usage terms' },
    { emoji: '🇪🇺', title: 'GDPR Notice', description: 'EU data protection rights disclosure' },
    { emoji: '🇺🇸', title: 'CCPA Disclosure', description: 'California consumer privacy notice' },
    { emoji: '🏥', title: 'HIPAA Authorization', description: 'Healthcare data authorization form' },
    { emoji: '🆔', title: 'Aadhaar Authorization', description: 'Aadhaar-linked consent (India)' },
    { emoji: '🔄', title: 'Data Sharing Agreement', description: 'Third-party data sharing terms' },
    { emoji: '🍪', title: 'Cookie Policy', description: 'Website tracking disclosure' },
    { emoji: '📅', title: 'Retention Policy', description: 'Data storage and deletion terms' },
    { emoji: '📋', title: 'Custom Document', description: 'Organization-specific requirements' },
  ],
  evidence: [
    { emoji: '✏️', title: 'Digital Signature', description: 'Capture hand-drawn signature on any device', tags: ['Standard', 'Legal'] },
    { emoji: '📱', title: 'Phone OTP', description: 'Verify identity via SMS one-time password', tags: ['Identity', 'Secure'] },
    { emoji: '📧', title: 'Email OTP', description: 'Verify identity via email one-time password', tags: ['Identity', 'Secure'] },
    { emoji: '🎥', title: 'Video Recording', description: 'Record consent acknowledgment video', tags: ['High-value', 'Premium'] },
    { emoji: '🎙️', title: 'Audio Recording', description: 'Record voice consent acknowledgment', tags: ['Telephonic', 'Premium'] },
    { emoji: '☑️', title: 'Checkbox', description: 'Simple checkbox acceptance for low-risk consent', tags: ['Simple', 'Basic'] },
  ],
  roles: [
    { emoji: '👑', title: 'ADMIN', description: 'Full platform access including user management, tenant settings, and all administrative functions', color: volcano[6] },
    { emoji: '🛡️', title: 'CONSENT_MANAGER', description: 'Manage templates, projects, consent artifacts, and withdrawal requests', color: blueGrey[7] },
    { emoji: '👁️', title: 'DATA_CONTROLLER', description: 'View consent artifacts, generate form URLs, and access analytics', color: jadeGreen[6] },
    { emoji: '📖', title: 'VIEWER', description: 'Read-only access to consent artifacts and basic analytics', color: neutral[6] },
  ],
};

// ===== COMPONENT =====

export const KeyConceptsGuide: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ConceptTab>('entities');

  const renderEntities = () => (
    <>
      <DiagramSection>
        <DiagramTitle>🔄 How Entities Relate</DiagramTitle>
        <RelationshipDiagram>
          <EntityBox $color={blueGrey[7]}>
            <div className="emoji">🏢</div>
            <div className="label">Tenant</div>
            <div className="sublabel">Organization</div>
          </EntityBox>
          <Arrow>
            <div className="line" />
            <div className="label">Contains</div>
          </Arrow>
          <EntityBox $color={volcano[6]}>
            <div className="emoji">🛡️</div>
            <div className="label">Fiduciary</div>
            <div className="sublabel">Data Handler</div>
          </EntityBox>
          <Arrow>
            <div className="line" />
            <div className="label">Manages</div>
          </Arrow>
          <EntityBox $color={lightBlue[5]}>
            <div className="emoji">📁</div>
            <div className="label">Project</div>
            <div className="sublabel">Initiative</div>
          </EntityBox>
          <Arrow>
            <div className="line" />
            <div className="label">Uses</div>
          </Arrow>
          <EntityBox $color={jadeGreen[5]}>
            <div className="emoji">📝</div>
            <div className="label">Template</div>
            <div className="sublabel">Document</div>
          </EntityBox>
        </RelationshipDiagram>
      </DiagramSection>

      <ConceptGrid>
        {concepts.entities.map((concept, index) => (
          <ConceptItem key={index} $color={concept.color}>
            <h3>
              <span className="emoji">{concept.emoji}</span>
              {concept.title}
            </h3>
            <p>{concept.description}</p>
            <div className="example">
              <strong>💡</strong> {concept.example}
            </div>
          </ConceptItem>
        ))}
      </ConceptGrid>

      <DiagramSection>
        <DiagramTitle>📊 Visual Hierarchy</DiagramTitle>
        <InfographicViewer syntax={TENANT_HIERARCHY_SYNTAX} height={400} />
      </DiagramSection>
    </>
  );

  const renderDocuments = () => (
    <>
      <InfographicViewer syntax={DOCUMENT_TYPES_SYNTAX} height={380} />
      
      <DefinitionList>
        {concepts.documents.map((doc, index) => (
          <DefinitionItem key={index}>
            <div className="icon">{doc.emoji}</div>
            <div className="content">
              <h4>{doc.title}</h4>
              <p>{doc.description}</p>
            </div>
          </DefinitionItem>
        ))}
      </DefinitionList>

      <InfoAlert>
        <span className="icon">💡</span>
        <div className="content">
          <strong>Tip:</strong> Use a layered approach by combining multiple document types for comprehensive 
          compliance coverage. For example, combine Privacy Policy + Consent Text + GDPR Notice for 
          EU data collection.
        </div>
      </InfoAlert>
    </>
  );

  const renderEvidence = () => (
    <>
      <InfographicViewer syntax={EVIDENCE_TYPES_SYNTAX} height={350} />
      
      <DefinitionList>
        {concepts.evidence.map((evidence, index) => (
          <DefinitionItem key={index}>
            <div className="icon">{evidence.emoji}</div>
            <div className="content">
              <h4>{evidence.title}</h4>
              <p>{evidence.description}</p>
              <div className="tags">
                {evidence.tags.map((tag, i) => (
                  <Tag key={i} $color={i === 0 ? jadeGreen[5] : blueGrey[6]}>{tag}</Tag>
                ))}
              </div>
            </div>
          </DefinitionItem>
        ))}
      </DefinitionList>

      <InfoAlert>
        <span className="icon">🔒</span>
        <div className="content">
          <strong>Multi-Evidence Support:</strong> You can require multiple evidence types for a single 
          consent. For example, combine Digital Signature + Phone OTP for enhanced identity verification 
          and legal protection.
        </div>
      </InfoAlert>
    </>
  );

  const renderRoles = () => (
    <>
      <InfographicViewer syntax={USER_ROLES_SYNTAX} height={350} />
      
      <DefinitionList>
        {concepts.roles.map((role, index) => (
          <DefinitionItem key={index}>
            <div className="icon" style={{ background: `${role.color}15` }}>{role.emoji}</div>
            <div className="content">
              <h4>{role.title}</h4>
              <p>{role.description}</p>
              <div className="tags">
                <Tag $color={role.color}>Level {concepts.roles.length - index}</Tag>
              </div>
            </div>
          </DefinitionItem>
        ))}
      </DefinitionList>

      <InfoAlert>
        <span className="icon">ℹ️</span>
        <div className="content">
          <strong>Role Hierarchy:</strong> Roles follow a hierarchical permission model. Higher roles 
          inherit all permissions from lower roles. An ADMIN has all permissions that CONSENT_MANAGER, 
          DATA_CONTROLLER, and VIEWER have.
        </div>
      </InfoAlert>
    </>
  );

  return (
    <Container>
      {/* Overview Infographic */}
      <ConceptCard>
        <SectionHeader>
          <IconCircle $color={`${blueGrey[1]}`}>📚</IconCircle>
          <SectionTitle>
            <h2>Key Platform Concepts</h2>
            <p>Understanding the building blocks of consent management</p>
          </SectionTitle>
        </SectionHeader>

        <InfographicViewer syntax={KEY_CONCEPTS_SYNTAX} height={400} />
      </ConceptCard>

      {/* Detailed Concepts */}
      <ConceptCard>
        <TabContainer>
          <Tab $active={activeTab === 'entities'} onClick={() => setActiveTab('entities')}>
            🏢 Core Entities
          </Tab>
          <Tab $active={activeTab === 'documents'} onClick={() => setActiveTab('documents')}>
            📄 Document Types
          </Tab>
          <Tab $active={activeTab === 'evidence'} onClick={() => setActiveTab('evidence')}>
            ✅ Evidence Types
          </Tab>
          <Tab $active={activeTab === 'roles'} onClick={() => setActiveTab('roles')}>
            👥 User Roles
          </Tab>
        </TabContainer>

        {activeTab === 'entities' && renderEntities()}
        {activeTab === 'documents' && renderDocuments()}
        {activeTab === 'evidence' && renderEvidence()}
        {activeTab === 'roles' && renderRoles()}
      </ConceptCard>
    </Container>
  );
};

export default KeyConceptsGuide;
